import { useEffect, useRef, useState } from 'react';
import { didReceiveResponse } from '@namiml/web-sdk';
import { apiTrafficStore, RECORD_LIMIT, TrafficRow } from '../api-traffic';

/** A transport failure has no status to show, so it gets a glyph rather than a `0`. */
function statusLabel(row: TrafficRow): string {
  return didReceiveResponse(row) ? String(row.statusCode) : '—';
}

/** Status band, matching the phone app and the two native tabs. */
function statusTone(row: TrafficRow): 'ok' | 'neutral' | 'bad' {
  if (!didReceiveResponse(row)) return 'bad';
  if (row.statusCode >= 200 && row.statusCode < 300) return 'ok';
  if (row.statusCode >= 300 && row.statusCode < 400) return 'neutral';
  return 'bad';
}

function formatDuration(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
}

function formatTime(timestamp: Date): string {
  return timestamp.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const Row = ({ row }: { row: TrafficRow }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="stack">
      {/*
        A `button`, not a styled `div`. It carries the app's `tv-button` class, which
        supplies the `:focus::after` ring every other focusable surface here uses — so the
        row is focusable AND visibly focused by construction. Android's port of this tab
        shipped rows that took focus but drew nothing, and the d-pad walked the list
        invisibly on the TV emulator (NAM-4447).
      */}
      <button
        type="button"
        className="tv-button api-traffic-row"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        data-testid={`api-traffic-row-${row.seq}`}
      >
        <span className="api-traffic-method">{row.method}</span>
        <span className="api-traffic-path">
          {row.path}
          {row.kind === 'cdn' && <span className="api-traffic-tag">CDN</span>}
        </span>
        <span className={`api-traffic-status api-traffic-status-${statusTone(row)}`}>
          {statusLabel(row)}
        </span>
        <span className="api-traffic-meta">{formatDuration(row.durationMs)}</span>
        <span className="api-traffic-meta">{formatTime(row.timestamp)}</span>
      </button>

      {/*
        Expands in place rather than opening an overlay: nothing on this screen should need
        a pointer to dismiss it.
      */}
      {open && (
        <div className="api-traffic-detail">
          <div className="panel-subtitle">URL</div>
          <div className="api-traffic-detail-value">{row.url}</div>

          {row.errorDescription && (
            <>
              <div className="panel-subtitle">Error</div>
              <div className="api-traffic-detail-value api-traffic-detail-error">
                {row.errorDescription}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ApiTrafficPage = () => {
  const [rows, setRows] = useState<TrafficRow[]>([]);
  const [dropped, setDropped] = useState(false);
  const [hasRows, setHasRows] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return apiTrafficStore.subscribe((next) => {
      setRows(next);
      setHasRows(next.length > 0);
      setDropped(apiTrafficStore.hasDroppedRecords());
    });
  }, []);

  /*
    Send focus into the list rather than leaving it on Clear.

    On Android, focus arriving from the nav landed on the right-aligned Clear button and
    the list was only reachable by pressing DOWN from there — a path nobody would guess
    (NAM-4447). Focusing the first row once rows exist makes the common case (read the
    traffic) the default; Clear stays reachable by moving up.

    Keyed on `hasRows` rather than `rows.length` so this fires once when the list first
    fills, not on every record — refocusing mid-stream would fight the viewer while
    traffic is arriving.
  */
  useEffect(() => {
    if (!hasRows) return;
    listRef.current?.querySelector<HTMLButtonElement>('.api-traffic-row')?.focus();
  }, [hasRows]);

  return (
    <div className="screen-root">
      <div className="panel stack">
        <h1 className="panel-title">API Traffic</h1>
        <p className="panel-subtitle">
          Every request the SDK made, newest first. Select a row for the full URL.
        </p>

        <div className="inline-actions">
          <button
            type="button"
            className="tv-button"
            data-variant="ghost"
            onClick={() => apiTrafficStore.clear()}
            data-testid="api-traffic-clear"
          >
            Clear
          </button>
        </div>

        {dropped && (
          <p className="panel-subtitle">
            Showing the most recent {RECORD_LIMIT} requests. Older ones have been dropped.
          </p>
        )}

        {rows.length === 0 ? (
          <div className="panel-subtitle">
            No requests recorded yet. Traffic appears here once the SDK is configured.
          </div>
        ) : (
          <div className="stack" ref={listRef}>
            {rows.map((row) => (
              <Row key={row.seq} row={row} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiTrafficPage;
